import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { enqueueSnackbar } from '../../../../../components/GlobalSnackbar/snackbarStore';
import { isSupabaseError } from '../../../../../constants/supabaseErrors';
import { useSpace } from '../../../../../hooks/useSpace';
import type { DisplayedSubjectQuote } from '../../../../../types';
import { keys as documentKeys } from '../../../../documents/api/keys';
import { useDeleteDocuments } from '../../../../documents/api/useDeleteDocuments';
import useDeleteFilesFromGoogleDrive from '../../../../documents/api/useDeleteFilesFromGoogleDrive';
import { keys } from '../../../api/keys';
import { useUpdateSubjectQuoteStatus } from '../../../api/useUpdateSubjectQuoteStatus';

type Props = {
  quote: DisplayedSubjectQuote;
  subjectId: string;
  onClose: () => void;
};

export default function useResetQuoteModal(props: Props) {
  const { currentSpace } = useSpace();
  const queryClient = useQueryClient();
  const { mutateAsync: updateQuoteStatus, isPending: isUpdatePending } = useUpdateSubjectQuoteStatus();
  const { mutateAsync: deleteDocuments, isPending: isDeletePending } = useDeleteDocuments();
  const { mutateAsync: deleteFilesFromDrive, isPending: isDrivePending } = useDeleteFilesFromGoogleDrive();

  const [serverError, setServerError] = useState<string | null>(null);

  const bpaDoc = props.quote.documents?.find(
    (doc) =>
      doc.file_url === props.quote.approved_document_url ||
      doc.document_types?.name?.toLowerCase() === 'bon pour accord' ||
      doc.name?.toLowerCase().includes('accord') ||
      doc.name?.toLowerCase().includes('signé')
  );

  async function handleKeepDocument() {
    setServerError(null);

    try {
      await updateQuoteStatus({
        quoteId: props.quote.id,
        subjectId: props.subjectId,
        status: 'pending',
        clearApprovedDocumentUrl: false,
      });

      enqueueSnackbar('Devis remis en attente.', 'info');
      props.onClose();
    } catch (err: unknown) {
      const message = isSupabaseError(err) ? err.message : 'Une erreur est survenue.';
      setServerError(message);
    }
  }

  async function handleDeleteDocument() {
    setServerError(null);

    try {
      const urlsToDelete = [
        bpaDoc?.file_url,
        props.quote.approved_document_url,
      ].filter((url): url is string => Boolean(url));

      if (currentSpace && urlsToDelete.length > 0) {
        try {
          await deleteFilesFromDrive({
            fileUrls: Array.from(new Set(urlsToDelete)),
            spaceId: currentSpace.id,
          });
        } catch (err) {
          console.warn('Could not delete file from Google Drive:', err);
        }
      }

      if (bpaDoc?.id) {
        await deleteDocuments({ ids: [bpaDoc.id] });
      }

      await updateQuoteStatus({
        quoteId: props.quote.id,
        subjectId: props.subjectId,
        status: 'pending',
        clearApprovedDocumentUrl: true,
      });

      queryClient.invalidateQueries({ queryKey: keys.subTopicQuotes(props.subjectId) });
      queryClient.invalidateQueries({ queryKey: keys.subTopic(props.subjectId) });
      queryClient.invalidateQueries({ queryKey: documentKeys.getDocumentsBySubTopicId(props.subjectId) });
      queryClient.invalidateQueries({ queryKey: [...keys.all, 'activityTimeline'] });

      enqueueSnackbar('Devis remis en attente et document supprimé.', 'info');
      props.onClose();
    } catch (err: unknown) {
      const message = isSupabaseError(err) ? err.message : 'Une erreur est survenue lors de la suppression.';
      setServerError(message);
    }
  }

  const isPending = isUpdatePending || isDeletePending || isDrivePending;

  function handleClose() {
    if (!isPending) {
      props.onClose();
    }
  }

  return {
    handleClose,
    handleKeepDocument,
    handleDeleteDocument,
    isPending,
    serverError,
    documentName: bpaDoc?.name || 'Bon pour accord',
  };
}
